import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { prefersLessData, usePrefersReducedMotion } from "./calm";

/** The figures that exist (public/onboarding/hairline/<name>.js), and which one each step shows. */
export type FigureName = "house" | "brand" | "building" | "asset" | "spaces" | "plan" | "escape" | "upkeep";

interface Props {
  name: FigureName;
  /**
   * Changes when the step changes. The figure of the new step starts from the beginning: a new figure is a new
   * frame anyway, the same figure (two steps share one) is restarted.
   */
  cycle?: string;
  /** The tour walks once and the result stays (the welcome and closing scenes); the step panel loops. */
  loop?: boolean;
  className?: string;
  /** Called once the figure reports it is drawn. */
  onReady?: () => void;
  /** The figure's beat: 0 the input arrives, 1 it is processed, 2 the result stands. */
  onBeat?: (beat: 0 | 1 | 2) => void;
  /** The one-sentence read-out of what is happening now ("" at rest). */
  onRead?: (text: string) => void;
}

/** The tour starts this long after the figure has reported ready. */
export const AUTO_PLAY_DELAY_MS = 600;

/**
 * After the tour has arrived at its last stop the figure still draws its result; the hold counts from the figure's
 * own "result" beat. Should that beat not come, the hold starts this much later anyway.
 */
export const RESULT_GRACE_MS = 1500;

/**
 * Right after a restart the figure settles back to rest while it is faded out and may report a middle beat
 * on the way; beats other than "input arrives" are not passed on for this long (the first real one is > 1.5 s away).
 */
export const RESTART_QUIET_MS = 800;

/** How long the result stands, to be read, before the loop starts over (per figure; never more than MAX_HOLD_MS). */
export const MAX_HOLD_MS = 5000;
export const HOLD_MS: Record<FigureName, number> = {
  house: 3500,
  brand: 3500,
  building: 3500,
  asset: 3500,
  spaces: 3500,
  plan: 3500,
  escape: 3500,
  upkeep: 3500,
};

/**
 * A Hairline figure in an iframe (same origin): `public/onboarding/hairline/embed.html` loads the
 * kernel and `<name>.js`. The frame only starts loading after the first paint, a calm static drawing
 * stands in until the figure reports ready (and stays if it never does). The figure loops: it walks its
 * tour, the result stands for a few seconds, the drawing fades out and in and it walks again. Nothing is
 * steered by a pointer (the frame does not take one), so a hover cannot disturb the loop. It does not loop
 * under reduced motion or the data saver (the result stands, still), and it holds still while the tab is in
 * the background or the figure is out of sight, and goes on from there. The kernel is never touched.
 */
export default function HairlineFigure({ name, cycle, loop = true, className, onReady, onBeat, onRead }: Props) {
  const { t, i18n } = useTranslation();
  const reduced = usePrefersReducedMotion();
  const frame = useRef<HTMLIFrameElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [armed, setArmed] = useState(false);
  const [ready, setReady] = useState(false);
  const lang = i18n.language?.startsWith("en") ? "en" : "de";
  /** Set by the loop below; starts the figure from the beginning again (a step change within one figure). */
  const startOver = useRef<(() => void) | null>(null);

  const send = (message: Record<string, unknown>) => frame.current?.contentWindow?.postMessage(message, window.location.origin);

  // not before the first paint: the page itself comes first
  useEffect(() => {
    const frameId = requestAnimationFrame(() => setArmed(true));
    return () => cancelAnimationFrame(frameId);
  }, []);

  useEffect(() => {
    setReady(false);
    const hold = Math.min(HOLD_MS[name] ?? 3500, MAX_HOLD_MS);
    let playing = false;
    let animate = false;
    let timer = 0;
    let pending: { run: () => void; left: number; at: number } | null = null;
    let visible = true;
    let suspended = false;
    let arrived = false;
    let quietUntil = 0;
    const active = () => visible && !document.hidden;

    const fire = () => {
      const job = pending;
      pending = null;
      timer = 0;
      job?.run();
    };
    /** Runs `run` after `ms` of the figure being on show; the clock stands still while it is not. */
    const after = (ms: number, run: () => void) => {
      window.clearTimeout(timer);
      pending = { run, left: ms, at: Date.now() };
      timer = active() ? window.setTimeout(fire, ms) : 0;
    };
    const suspend = () => {
      if (suspended) return;
      suspended = true;
      if (timer && pending) {
        window.clearTimeout(timer);
        timer = 0;
        pending.left = Math.max(0, pending.left - (Date.now() - pending.at));
      }
      if (playing) send({ hl: "pause" });
    };
    const wake = () => {
      if (!suspended) return;
      suspended = false;
      if (pending && !timer) {
        pending.at = Date.now();
        timer = window.setTimeout(fire, pending.left);
      }
      if (playing) send({ hl: "resume" });
    };
    const sync = () => (active() ? wake() : suspend());

    const restart = () => {
      playing = true;
      arrived = false;
      quietUntil = Date.now() + RESTART_QUIET_MS;
      onBeat?.(0);
      onRead?.("");
      send({ hl: "restart" });
    };
    startOver.current = () => {
      if (!animate) return;
      playing = false;
      arrived = false;
      after(AUTO_PLAY_DELAY_MS, restart);
    };

    const listener = (event: MessageEvent) => {
      // only this frame's own messages, from our own origin
      if (event.origin !== window.location.origin || event.source !== frame.current?.contentWindow) return;
      const data = event.data;
      if (data?.type === "hairline-beat" && data.figure === name && (data.beat === 0 || data.beat === 1 || data.beat === 2)) {
        if (data.beat !== 0 && Date.now() < quietUntil) return;
        // the result is on show: now it stands for the hold time
        if (data.beat === 2 && arrived && animate && loop) {
          arrived = false;
          after(hold, restart);
        }
        onBeat?.(data.beat);
        return;
      }
      if (data?.hl === "read" && data.figure === name) {
        onRead?.(typeof data.text === "string" ? data.text.slice(0, 200) : "");
        return;
      }
      if (data?.hl === "done" && data.figure === name) {
        // the result stands for a moment, then the loop starts over
        playing = false;
        arrived = true;
        if (animate && loop) after(RESULT_GRACE_MS + hold, restart);
        return;
      }
      if (data?.hl === "ready") {
        setReady(true);
        onReady?.();
        animate = !reduced && !prefersLessData();
        if (animate) {
          after(AUTO_PLAY_DELAY_MS, () => {
            playing = true;
            send({ hl: "play" });
          });
        } else send({ hl: "result" });
      }
    };
    window.addEventListener("message", listener);
    document.addEventListener("visibilitychange", sync);
    let observer: IntersectionObserver | undefined;
    if (typeof IntersectionObserver !== "undefined" && box.current) {
      observer = new IntersectionObserver((entries) => {
        visible = entries[entries.length - 1]?.isIntersecting ?? true;
        sync();
      });
      observer.observe(box.current);
    }
    return () => {
      window.clearTimeout(timer);
      startOver.current = null;
      observer?.disconnect();
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("message", listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, reduced, loop]);

  // a step change within the same figure: from the beginning
  const firstCycle = useRef(true);
  useEffect(() => {
    if (firstCycle.current) {
      firstCycle.current = false;
      return;
    }
    startOver.current?.();
  }, [cycle]);

  return (
    <div ref={box} className={cn("relative aspect-[5/4] w-full select-none", className)} data-testid="hairline-figure" data-figure={name} data-ready={ready ? "true" : "false"}>
      {/* the stand-in: the same building in a few quiet lines, until (or unless) the figure is there */}
      <StaticFigure hidden={ready} />
      {armed && (
        <iframe
          ref={frame}
          title={t("onboardingPage.figure.title")}
          src={`/onboarding/hairline/embed.html?f=${name}&lang=${lang}`}
          loading="eager"
          scrolling="no"
          tabIndex={-1}
          aria-hidden="true"
          sandbox="allow-scripts allow-same-origin"
          referrerPolicy="no-referrer"
          className={cn("pointer-events-none absolute inset-0 h-full w-full border-0 bg-transparent outline-none transition-opacity duration-500", ready ? "opacity-100" : "opacity-0")}
          style={{ colorScheme: "light" }}
        />
      )}
    </div>
  );
}

/** A building in section, in hairlines: what shows while the figure loads, and when it cannot. */
export function StaticFigure({ hidden }: { hidden?: boolean }) {
  return (
    <svg
      viewBox="0 0 400 320"
      aria-hidden="true"
      className={cn("absolute inset-0 h-full w-full transition-opacity duration-500", hidden ? "opacity-0" : "opacity-100")}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <g className="text-border" strokeWidth="1">
        <path d="M120 250h160M104 266h192" />
        <path d="M130 250V120l70-30 70 30v130" />
        <path d="M130 198h140M130 160h140M130 122h140" className="text-muted-foreground/50" />
      </g>
      <g className="text-foreground" strokeWidth="1.2">
        <path d="M118 252l82 36 82-36" opacity="0.5" />
        <path d="M200 90v198" opacity="0.35" />
        <rect x="150" y="212" width="22" height="26" rx="4" />
        <rect x="228" y="174" width="26" height="22" rx="4" />
        <circle cx="200" cy="90" r="3" fill="currentColor" stroke="none" />
      </g>
    </svg>
  );
}
